/**
 * PublicListToolbar
 *
 * Persistent search + filter bar for the public artifact list. The name search
 * is always visible and in the same place (`[ search | filter ]`); it filters
 * client-side on every tab. The filter icon — and its collapsible structured
 * filter fields — appear only on a per-kind tab (the "All" tab has no per-kind
 * filters). Viewer filters are server-side: `onViewerFiltersChange` triggers a
 * refetch upstream.
 *
 * @module components/share/public/PublicArtifactList/parts/PublicListToolbar
 */
import { memo, useCallback, useMemo, useState } from 'react';
import { Collapse, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { SearchInput } from '@/components/common/SearchInput';
import {
  NoteFilterFields,
  ObstructionFilterFields,
  TaskFilterFields,
} from '@/components/share/filterFields';
import { FilterIconButton } from '@/components/ui/FilterIconButton';
import { SortControls } from '@/components/ui/SortControls';
import type { SortOption } from '@/components/ui/SortControls';
import {
  DEFAULT_NOTE_SORT_BY,
  DEFAULT_OBSTRUCTION_SORT_BY,
  DEFAULT_SORT_DESCENDING,
  DEFAULT_TASK_SORT_BY,
} from '@/constants/sortOptions';
import type { PublicShareConfig, ShareViewerFilters } from '@/types/share';

import {
  getNoteSortOptions,
  getObstructionSortOptions,
  getTaskSortOptions,
} from './publicSortOptions';
import {
  hasActiveViewerFilters,
  type PublicListTab,
} from './shareViewerFilterUtils';
import styles from './PublicListToolbar.module.css';

interface SortProps {
  options: SortOption[];
  sortBy: string;
  sortDescending: boolean;
  onSortByChange: (value: string) => void;
  onSortDescendingChange: (value: boolean) => void;
}

interface PublicListToolbarProps {
  activeTab: PublicListTab;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  viewerFilters: ShareViewerFilters;
  onViewerFiltersChange: (filters: ShareViewerFilters) => void;
  /** Share config — supplies the owner's allowed filter subsets per dimension. */
  config: PublicShareConfig;
}

export const PublicListToolbar = memo(function PublicListToolbar({
  activeTab,
  searchQuery,
  onSearchChange,
  viewerFilters,
  onViewerFiltersChange,
  config,
}: PublicListToolbarProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Lazy-mount: the filter inputs stay unmounted until first opened (the public
  // page is a hot, anonymous path), then stay mounted so the close animation
  // still plays.
  const [mounted, setMounted] = useState(false);

  const patch = useCallback(
    (next: Partial<ShareViewerFilters>): void => {
      onViewerFiltersChange({ ...viewerFilters, ...next });
    },
    [viewerFilters, onViewerFiltersChange]
  );

  // Per-tab server-side sort props. `null` for the cross-kind "all" tab (no
  // sort control there). Person-based fields are stripped in the option
  // resolvers when the share anonymizes personal data.
  const sortProps = useMemo<SortProps | null>(() => {
    const { showPersonalData } = config;
    switch (activeTab) {
      case 'task': {
        const effectiveSortBy =
          viewerFilters.taskSortBy ?? DEFAULT_TASK_SORT_BY;
        const effectiveDescending =
          viewerFilters.taskSortDescending ?? DEFAULT_SORT_DESCENDING;
        return {
          options: getTaskSortOptions(showPersonalData),
          sortBy: effectiveSortBy,
          sortDescending: effectiveDescending,
          onSortByChange: (value) =>
            patch({
              taskSortBy: value,
              taskSortDescending: effectiveDescending,
            }),
          onSortDescendingChange: (value) =>
            patch({ taskSortBy: effectiveSortBy, taskSortDescending: value }),
        };
      }
      case 'note': {
        const effectiveSortBy =
          viewerFilters.noteSortBy ?? DEFAULT_NOTE_SORT_BY;
        const effectiveDescending =
          viewerFilters.noteSortDescending ?? DEFAULT_SORT_DESCENDING;
        return {
          options: getNoteSortOptions(showPersonalData),
          sortBy: effectiveSortBy,
          sortDescending: effectiveDescending,
          onSortByChange: (value) =>
            patch({
              noteSortBy: value,
              noteSortDescending: effectiveDescending,
            }),
          onSortDescendingChange: (value) =>
            patch({ noteSortBy: effectiveSortBy, noteSortDescending: value }),
        };
      }
      case 'obstruction': {
        const effectiveSortBy =
          viewerFilters.obstructionSortBy ?? DEFAULT_OBSTRUCTION_SORT_BY;
        const effectiveDescending =
          viewerFilters.obstructionSortDescending ?? DEFAULT_SORT_DESCENDING;
        return {
          options: getObstructionSortOptions(),
          sortBy: effectiveSortBy,
          sortDescending: effectiveDescending,
          onSortByChange: (value) =>
            patch({
              obstructionSortBy: value,
              obstructionSortDescending: effectiveDescending,
            }),
          onSortDescendingChange: (value) =>
            patch({
              obstructionSortBy: effectiveSortBy,
              obstructionSortDescending: value,
            }),
        };
      }
      default:
        return null;
    }
  }, [activeTab, viewerFilters, config, patch]);

  return (
    <div className={styles.toolbar}>
      <Group gap="xs" wrap="nowrap" align="center">
        <div className={styles.searchWrap}>
          <SearchInput
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={t('share.public.searchPlaceholder')}
          />
        </div>
        {activeTab !== 'all' && (
          <FilterIconButton
            hasActiveFilters={hasActiveViewerFilters(activeTab, viewerFilters)}
            opened={open}
            onClick={() => {
              setMounted(true);
              setOpen((current) => !current);
            }}
            tooltip={t('share.public.filtersTitle')}
            aria-label={t('share.public.filtersTitle')}
            size="input"
          />
        )}
      </Group>

      {activeTab !== 'all' && (
        <Collapse expanded={open}>
          {mounted && (
            <div className={styles.fields}>
              {activeTab === 'task' && (
                <TaskFilterFields
                  value={viewerFilters}
                  onPatch={patch}
                  allowedStatuses={config.allowedTaskStatuses}
                  allowedPriorities={config.allowedTaskPriorities}
                />
              )}
              {activeTab === 'note' && (
                <NoteFilterFields
                  value={viewerFilters}
                  onPatch={patch}
                  allowedColors={config.allowedNoteColors}
                />
              )}
              {activeTab === 'obstruction' && (
                <ObstructionFilterFields
                  value={viewerFilters}
                  onPatch={patch}
                  allowedTypes={config.allowedObstructionTypes}
                  allowedStatuses={config.allowedObstructionStatuses}
                  allowedSeverities={config.allowedObstructionSeverities}
                  allowedImpacts={config.allowedObstructionImpacts}
                />
              )}
              {sortProps && (
                <div className={styles.sortRow}>
                  <SortControls {...sortProps} />
                </div>
              )}
            </div>
          )}
        </Collapse>
      )}
    </div>
  );
});
